import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Clock, Send, CheckSquare, PenLine, ListChecks, Bell, CheckCheck, Plus, ChevronRight } from 'lucide-react';
import api from '../../../utils/api';
import {
  PageHeader, Panel, Button, Skeleton, Avatar, Banner, Empty, tone, money, useLoad, errMsg, fmtDate, fmtDateTime, hoursOf, todayIso,
} from '../kit';
import { EnsureLogTime, useLogTime, useOnTimeSaved } from './LogTimeProvider';
import { useScMe, nameOf, isLive, isMine, daysBetween, agoText, plural } from './participants';

// Overview = the coordinator's day ("My day"). One prioritised list of what needs this person now
// (late first, then due today, then the rest), each row with one action that does it; their own
// participants with a Log time button each; key dates coming up; and, only for people who claim or
// reconcile, a one-line strip of the office counts. Calendar and Reports & oversight are links.

const BASE = '/support-coordination';
const view = (q) => `${BASE}?${q}`;
const NOTE_DAYS = 30;      // a participant with no note for this long needs one
const SHOW_NEEDS = 8;
const SHOW_PEOPLE = 10;
const SETUP_CHECK_MAX = 30; // participants whose setup checklist is read for "setup isn't finished"

export default function ScOverview() {
  return <EnsureLogTime><MyDay /></EnsureLogTime>;
}

// Setup checklists for the given participants; returns the incomplete ones.
function useSetupGaps(ids) {
  const key = ids.join(',');
  const [state, setState] = useState({ key: '', gaps: [] });
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    Promise.allSettled(key.split(',').map((id) => api.get(`/sc/engagements/${id}/checklist`).then((r) => r.data)))
      .then((res) => {
        if (!live) return;
        setState({ key, gaps: res.filter((r) => r.status === 'fulfilled' && r.value && r.value.complete === false).map((r) => r.value) });
      });
    return () => { live = false; };
  }, [key]);
  return state.key === key ? state.gaps : [];
}

const longDate = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  if (!y) return '';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};
const dayChip = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return { wd: dt.toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'UTC' }), dm: dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }) };
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

function MyDay() {
  const navigate = useNavigate();
  const logTime = useLogTime();
  const me = useScMe();
  const perms = me.perms;
  const canLog = perms.includes('log_time') || perms.includes('view_all_hours');
  const canRef = perms.includes('manage_referrals');
  const isOffice = ['claiming', 'reconciliation'].some((p) => perms.includes(p));

  const ov = useLoad('/sc/overview');
  const day = useLoad('/support-coordination/my-day', me.userId ? { coordinator_id: me.userId } : null, { skip: !me.userId });
  const list = useLoad(BASE);
  const myHours = useLoad('/sc/hours/my', null, { skip: !perms.includes('log_time') });
  const today = (ov.data && ov.data.today) || todayIso();

  const { reload: reloadOv } = ov; const { reload: reloadDay } = day; const { reload: reloadList } = list; const { reload: reloadHours } = myHours;
  useOnTimeSaved(useCallback(() => { reloadOv(); reloadDay(); reloadList(); reloadHours(); }, [reloadOv, reloadDay, reloadList, reloadHours]));

  // My participants; someone with View all hours who has none of their own (an office manager,
  // the demo admin) sees everyone instead of an empty panel.
  const seeAll = perms.includes('view_all_hours');
  const ownRows = useMemo(() => (list.data || []).filter((r) => isLive(r) && isMine(r, me))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [list.data, me]);
  const everyone = !ownRows.length && seeAll;
  const myRows = useMemo(() => (everyone ? (list.data || []).filter(isLive).sort((a, b) => nameOf(a).localeCompare(nameOf(b))) : ownRows),
    [everyone, ownRows, list.data]);
  const setupGaps = useSetupGaps(useMemo(() => myRows.slice(0, SETUP_CHECK_MAX).map((r) => r.id), [myRows]));

  // Row actions are data ({ run: { type, ... } }) so the list can be memoised; this does them.
  const [busyKey, setBusyKey] = useState(null);
  const act = async (key, run) => {
    if (run.type === 'log') { logTime({ coordinationId: run.coordinationId, participantName: run.name }); return; }
    setBusyKey(key);
    if (run.type === 'take') {
      try {
        await api.put(`/sc/referrals/${run.ref.id}/assign`, { assigned_user_id: me.userId });
        toast.success(run.ref.referral_number ? `Referral ${run.ref.referral_number} is yours now.` : 'The referral is yours now.');
        reloadOv();
      } catch (e) { toast.error(errMsg(e, 'Couldn’t assign the referral to you. Open it and assign it there.')); }
    } else if (run.type === 'note') {
      try { await api.post(`/sc/notifications/${run.note.id}/read`); } catch { /* opening matters more than the read mark */ }
      if (run.note.link) navigate(run.note.link); else reloadOv();
    }
    setBusyKey(null);
  };

  // ── Needs you: one list, most urgent first ───────────────────────────────────────────────────
  const needs = useMemo(() => {
    const out = [];
    const sections = (ov.data && ov.data.sections) || {};
    // Referral follow-ups that are mine, or nobody's yet.
    ((sections.referrals && sections.referrals.items) || []).forEach((r) => {
      const unassigned = !r.assigned_user_id;
      if (!r.mine && !unassigned) return;
      const late = !!r.overdue;
      const num = r.referral_number ? ` ${r.referral_number}` : '';
      out.push({
        key: `ref-${r.id}`, icon: Send, rank: late ? 0 : 1, date: r.next_followup_date, tone: late ? 'red' : 'amber', late, soon: !late,
        what: unassigned ? `Referral${num}: unassigned — take it` : `Follow up referral${num}`,
        who: r.participant || null,
        due: late ? `${plural(Math.max(1, daysBetween(r.next_followup_date, today) || 1), 'day', 'days')} overdue` : 'Due today',
        action: unassigned && canRef && me.userId
          ? { label: 'Take it', run: { type: 'take', ref: r }, aria: `Take referral${num}` }
          : { label: 'Log follow-up', to: view(`view=referrals&referral=${r.id}`), aria: `Log follow-up for referral${num}` },
      });
    });

    // My hours: warnings on saved time (information only, nothing to submit or approve). A day over
    // the daily hours limit, or an entry outside the plan or service agreement dates.
    const cur = myHours.data;
    const seenDays = new Set();
    ((cur && cur.flags && cur.flags.items) || []).forEach((f) => {
      if (f.code !== 'daily_hours' && f.code !== 'outside_plan' && f.code !== 'outside_agreement') return;
      const daily = f.code === 'daily_hours';
      if (daily) { if (seenDays.has(f.note_date)) return; seenDays.add(f.note_date); }
      out.push({
        key: `hours-${f.code}-${daily ? f.note_date : f.entry_id}`, icon: Clock, rank: 2, date: f.note_date, tone: 'amber',
        what: daily ? `Over the daily hours limit on ${fmtDate(f.note_date)}`
          : `${f.code === 'outside_plan' ? 'Outside the plan dates' : 'Outside the service agreement dates'}: this time can’t be claimed`,
        who: daily ? null : (f.client_name || null),
        due: daily ? 'Saved. For information only.' : `Saved ${fmtDate(f.note_date)}`,
        action: { label: 'Open SC hours', to: view(`view=hours&period=${f.note_date}`), aria: `Open SC hours for ${fmtDate(f.note_date)}` },
      });
    });

    // My tasks due today or overdue.
    ((day.data && day.data.tasks) || []).forEach((t) => {
      const d = daysBetween(t.due_date, today);
      const late = d > 0;
      const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
      out.push({
        key: `task-${t.id}`, icon: CheckSquare, rank: late ? 0 : 1, date: t.due_date, tone: late ? 'red' : 'amber', late, soon: !late,
        what: t.title, who: name, whoTo: `${BASE}/${t.coordination_id}`,
        due: late ? `${plural(d, 'day', 'days')} overdue` : 'Due today',
        action: { label: 'Open task', to: `${BASE}/${t.coordination_id}`, aria: `Open task: ${t.title}` },
      });
    });

    // Participants with no note for 30 days or more.
    ((day.data && day.data.needNote) || []).forEach((n) => {
      const d = n.last_billable ? daysBetween(n.last_billable, today) : null;
      if (d != null && d < NOTE_DAYS) return;
      const name = `${n.first_name || ''} ${n.last_name || ''}`.trim();
      out.push({
        key: `note-${n.id}`, icon: PenLine, rank: 2, date: n.last_billable || '0000-00-00', tone: 'amber',
        what: d == null ? 'No notes yet' : `No note for ${d} days`, who: name, whoTo: `${BASE}/${n.id}`,
        due: d == null ? null : `Last note ${fmtDate(n.last_billable)}`,
        action: { label: 'Log time', run: { type: 'log', coordinationId: n.id, name }, aria: `Log time for ${name}` },
      });
    });

    // Unread notifications (forms submitted, claim results) — newest first.
    ((sections.notifications && sections.notifications.items) || []).forEach((n) => {
      out.push({
        key: `n-${n.id}`, icon: Bell, rank: 3, keepOrder: true, tone: 'blue',
        what: n.title, detail: n.body || null, due: fmtDateTime(n.created_at),
        action: { label: n.link ? 'Open' : 'Mark read', run: { type: 'note', note: n }, aria: `${n.link ? 'Open' : 'Mark read'}: ${n.title}` },
      });
    });

    // My participants whose setup isn't finished.
    setupGaps.forEach((c) => {
      const row = myRows.find((r) => String(r.id) === String(c.coordinationId));
      const left = (c.items || []).filter((i) => !i.done);
      if (!row || !left.length) return;
      const name = nameOf(row);
      out.push({
        key: `setup-${c.coordinationId}`, icon: ListChecks, rank: 4, keepOrder: true, tone: 'violet',
        what: 'Setup isn’t finished', who: name, whoTo: `${BASE}/${row.id}`,
        due: `${plural(left.length, 'step', 'steps')} left: ${left.map((i) => i.label.charAt(0).toLowerCase() + i.label.slice(1)).join(', ')}`,
        action: { label: 'Finish setup', to: `${BASE}/${row.id}`, aria: `Finish setup for ${name}` },
      });
    });

    return out.sort((a, b) => a.rank - b.rank || (a.keepOrder || b.keepOrder ? 0 : String(a.date || '').localeCompare(String(b.date || ''))));
  }, [ov.data, myHours.data, day.data, setupGaps, myRows, me.userId, canRef, today]);

  const [allNeeds, setAllNeeds] = useState(false);
  const [allPeople, setAllPeople] = useState(false);
  const needsLoading = (!ov.data && ov.loading) || (!me.loaded && me.loading) || (!!me.userId && !day.data && day.loading);
  const failed = [
    ov.error && { what: 'referrals, hours and updates', retry: reloadOv },
    day.error && { what: 'your tasks and notes', retry: reloadDay },
    me.error && { what: 'your permissions', retry: me.reload },
    myHours.error && { what: 'your hours', retry: reloadHours },
  ].filter(Boolean);

  // ── Coming up: key dates in the next 14 days ─────────────────────────────────────────────────
  const coming = useMemo(() => {
    const ev = [];
    ((day.data && day.data.keyDates) || []).forEach((k) => {
      const name = `${k.first_name || ''} ${k.last_name || ''}`.trim();
      [['Plan review', k.plan_review_due], ['Progress report due', k.progress_report_due], [k.next_release_info || 'Funding release', k.next_release_date]]
        .forEach(([label, d]) => {
          const n = d ? daysBetween(today, d) : null;
          if (n != null && n >= 0 && n <= 14) ev.push({ key: `${k.id}-${label}`, id: k.id, date: String(d).slice(0, 10), label, name, n });
        });
    });
    return ev.sort((a, b) => a.date.localeCompare(b.date));
  }, [day.data, today]);

  // ── Header ───────────────────────────────────────────────────────────────────────────────────
  const hoursText = myHours.data && myHours.data.summary
    ? `${hoursOf(myHours.data.summary.total_minutes)} h logged this pay period` : null;
  const title = me.firstName ? `${greeting()}, ${me.firstName}` : 'My day';
  const showPeople = canLog || myRows.length > 0;

  return (
    <div>
      <PageHeader title={title} sub={[longDate(today), hoursText].filter(Boolean).join(' · ')}
        actions={<>
          {canRef && <LinkBtn to={view('view=referrals')} variant="secondary" icon={Send} size="md">Log a referral</LinkBtn>}
          {canLog && <Button icon={Plus} onClick={() => logTime({})}>Log time</Button>}
        </>} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] xl:grid-cols-[minmax(0,1fr)_minmax(0,25rem)] gap-5 items-start">
        {/* Needs you — the focal point, first on every screen size */}
        <Panel title={<span className="inline-flex items-center gap-2">Needs you{needs.length > 0 && <span className="text-[11px] font-bold tabular-nums px-1.5 py-px rounded-full bg-slate-200/80 text-slate-700">{needs.length}</span>}</span>}
          sub={needs.length ? 'Most urgent first.' : undefined} pad={false}>
          {failed.length > 0 && (
            <div className="px-5 pb-3">
              <Banner tone="amber" title={`Couldn’t load ${failed.map((x) => x.what).join(', ')}`}
                action={<Button size="sm" variant="secondary" onClick={() => failed.forEach((x) => x.retry && x.retry())}>Try again</Button>}>
                The list below may be missing items until it loads.
              </Banner>
            </div>
          )}
          {needsLoading ? (
            <ul className="px-5 pb-4 space-y-3" aria-busy="true" aria-label="Loading">
              {[0, 1, 2, 3].map((i) => <li key={i} className="flex items-center gap-3"><Skeleton className="w-8 h-8 rounded-xl" /><div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-3/5" /><Skeleton className="h-3 w-2/5" /></div><Skeleton className="h-8 w-24" /></li>)}
            </ul>
          ) : !needs.length ? (
            <Empty icon={CheckCheck} title="You’re all caught up"
              action={canLog ? <Button icon={Plus} variant="secondary" onClick={() => logTime({})}>Log time</Button> : null}>
              No follow-ups, tasks or hours are waiting on you. New ones show here as they fall due.
            </Empty>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {(allNeeds ? needs : needs.slice(0, SHOW_NEEDS)).map((it) => (
                  <NeedRow key={it.key} it={it} busy={busyKey === it.key} onRun={() => act(it.key, it.action.run)} />
                ))}
              </ul>
              {needs.length > SHOW_NEEDS && (
                <div className="px-5 py-2 border-t border-slate-100">
                  <button type="button" onClick={() => setAllNeeds((v) => !v)} aria-expanded={allNeeds}
                    className="min-h-[44px] sm:min-h-0 sm:py-1.5 text-[13px] font-semibold text-indigo-700 hover:text-indigo-900 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                    {allNeeds ? 'Show fewer' : `Show all ${needs.length}`}
                  </button>
                </div>
              )}
            </>
          )}
        </Panel>

        {showPeople && (
          <div className="space-y-5 min-w-0">
            <Panel title={everyone ? 'Participants' : 'My participants'} sub={list.data ? (myRows.length ? plural(myRows.length, 'active participant', 'active participants') : undefined) : undefined}
              actions={<LinkBtn to={view('view=engagements')}>All participants</LinkBtn>} pad={false}>
              {list.error ? (
                <div className="px-5 pb-4"><Banner tone="red" title="Couldn’t load your participants" action={<Button size="sm" variant="secondary" onClick={reloadList}>Try again</Button>}>Check your connection, then try again.</Banner></div>
              ) : !list.data ? (
                <div className="px-5 pb-4 space-y-3" aria-busy="true" aria-label="Loading">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
              ) : !myRows.length ? (
                <p className="px-5 pb-5 text-[13px] text-slate-600">No participants are assigned to you. Find anyone in <Link to={view('view=engagements')} className="font-semibold text-indigo-700 hover:underline">Caseload</Link>.</p>
              ) : (
                <>
                  <ul className="divide-y divide-slate-100 border-t border-slate-100">
                    {(allPeople ? myRows : myRows.slice(0, SHOW_PEOPLE)).map((r) => (
                      <PersonRow key={r.id} r={r} today={today} canLog={canLog}
                        onLog={() => logTime({ coordinationId: r.id, participantName: nameOf(r) })} />
                    ))}
                  </ul>
                  {myRows.length > SHOW_PEOPLE && (
                    <div className="px-5 py-2 border-t border-slate-100">
                      <button type="button" onClick={() => setAllPeople((v) => !v)} aria-expanded={allPeople}
                        className="min-h-[44px] sm:min-h-0 sm:py-1.5 text-[13px] font-semibold text-indigo-700 hover:text-indigo-900 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                        {allPeople ? 'Show fewer' : `Show all ${myRows.length}`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </Panel>

            <Panel title="Coming up" sub="Key dates in the next 14 days" actions={<LinkBtn to={view('view=calendar')}>Calendar</LinkBtn>} pad={false}>
              {day.error ? (
                <p className="px-5 pb-5 text-[13px] text-slate-600">Couldn’t load key dates. <button type="button" onClick={reloadDay} className="font-semibold text-indigo-700 hover:underline">Try again</button></p>
              ) : !day.data && (day.loading || !me.loaded) ? (
                <div className="px-5 pb-4 space-y-3" aria-busy="true" aria-label="Loading">{[0, 1].map((i) => <Skeleton key={i} className="h-9" />)}</div>
              ) : !coming.length ? (
                <p className="px-5 pb-5 text-[13px] text-slate-600">No plan reviews, reports or funding releases in the next 14 days.</p>
              ) : (
                <ul className="divide-y divide-slate-100 border-t border-slate-100">
                  {coming.map((e) => {
                    const c = dayChip(e.date);
                    return (
                      <li key={e.key}>
                        <Link to={`${BASE}/${e.id}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500">
                          <span className={clsx('w-12 flex-shrink-0 rounded-lg text-center py-1 ring-1 ring-inset', e.n <= 2 ? 'bg-amber-50 ring-amber-200 text-amber-900' : 'bg-violet-50/70 ring-violet-100 text-violet-900')}>
                            <span className="block text-[11px] font-semibold uppercase leading-4">{c.wd}</span>
                            <span className="block text-[12px] font-semibold leading-4 tabular-nums">{c.dm}</span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] font-semibold text-slate-900 truncate">{e.label}</span>
                            <span className="block text-[12px] text-slate-500 truncate">{e.name} · {e.n === 0 ? 'today' : e.n === 1 ? 'tomorrow' : `in ${e.n} days`}</span>
                          </span>
                          <ChevronRight size={15} className="text-slate-400 flex-shrink-0" aria-hidden />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </div>
        )}

        {isOffice && <OfficeStrip perms={perms} sections={(ov.data && ov.data.sections) || null} loading={!ov.data && ov.loading} />}
      </div>
    </div>
  );
}

// One row of "Needs you": icon, one line on what, who and when, and the one action that does it.
function NeedRow({ it, busy, onRun }) {
  const c = tone(it.tone);
  const Icon = it.icon;
  const a = it.action;
  return (
    <li className="flex items-start gap-3 px-4 sm:px-5 py-3">
      <span className={clsx('mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0', c.icon)}><Icon size={15} aria-hidden /></span>
      {/* Phones: the action sits under the text so the text keeps the full width. */}
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-semibold text-slate-900 leading-5 break-words">{it.what}</p>
          <p className="mt-0.5 text-[12.5px] text-slate-500 leading-5">
            {[
              it.who && (it.whoTo
                ? <Link key="w" to={it.whoTo} className="font-medium text-slate-700 hover:text-indigo-700 hover:underline underline-offset-2">{it.who}</Link>
                : <span key="w" className="font-medium text-slate-700">{it.who}</span>),
              it.meta && <span key="m" className="tabular-nums">{it.meta}</span>,
              it.due && <span key="d" className={clsx(it.late && 'font-semibold text-rose-700', it.soon && 'font-semibold text-amber-800')}>{it.due}</span>,
            ].filter(Boolean).reduce((acc, el, i) => (i ? [...acc, <span key={`s${i}`} aria-hidden> · </span>, el] : [el]), [])}
          </p>
          {it.detail && <p className="mt-0.5 text-[12.5px] text-slate-600 line-clamp-2">{it.detail}</p>}
        </div>
        <div className="flex-shrink-0">
          {a.to
            ? <LinkBtn to={a.to} variant="secondary" aria-label={a.aria}>{a.label}</LinkBtn>
            : <Button size="sm" variant="secondary" onClick={onRun} loading={busy} aria-label={a.aria}>{a.label}</Button>}
        </div>
      </div>
    </li>
  );
}

function PersonRow({ r, today, canLog, onLog }) {
  const name = nameOf(r);
  const noteDays = r.last_note ? daysBetween(r.last_note, today) : null;
  const planLeft = r.plan_end ? daysBetween(today, r.plan_end) : null;
  const plan = planLeft == null ? null
    : planLeft < 0 ? <span className="font-semibold text-rose-700">Plan ended {fmtDate(r.plan_end)}</span>
      : planLeft <= 30 ? <span className="font-semibold text-amber-800">Plan ends in {plural(planLeft, 'day', 'days')}</span>
        : <span>Plan ends {fmtDate(r.plan_end)}</span>;
  return (
    <li className="flex items-center gap-3 px-5 py-2.5">
      <Avatar first={r.first_name} last={r.last_name} size={30} />
      <div className="flex-1 min-w-0">
        <Link to={`${BASE}/${r.id}`} className="block text-[13.5px] font-semibold text-slate-900 truncate rounded hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">{name}</Link>
        <p className="text-[12px] text-slate-500 leading-[18px]">{r.last_note ? `Last note ${agoText(noteDays)}` : 'No notes yet'}</p>
        {plan && <p className="text-[12px] text-slate-500 leading-[18px]">{plan}</p>}
      </div>
      {canLog && <Button size="sm" variant="secondary" icon={Clock} onClick={onLog} aria-label={`Log time for ${name}`}>Log time</Button>}
    </li>
  );
}

// Office counts on one line, only the ones that need someone. All clear = one line.
function OfficeStrip({ perms, sections, loading }) {
  const cl = sections && sections.claims;
  const claiming = perms.includes('claiming');
  const recon = perms.includes('reconciliation');
  const items = [
    claiming && cl && { key: 'fix', label: 'Fix list', value: cl.fixList, to: view('view=claims'), tone: 'red' },
    claiming && cl && { key: 'ready', label: 'Ready to claim', value: cl.unclaimedAmount, money: true, to: view('view=claims'), tone: 'emerald' },
    claiming && cl && { key: 'near', label: 'Near the 90-day limit', value: cl.nearLimit, to: view('view=claims'), tone: 'amber' },
    claiming && cl && { key: 'rej', label: 'Rejected lines', value: cl.rejected, to: view('view=claims'), tone: 'red' },
    (claiming || recon) && cl && { key: 'inv', label: 'Overdue invoices', value: cl.overdueInvoices, to: view('view=claims'), tone: 'red' },
  ].filter(Boolean).filter((x) => Number(x.value) > 0);
  const claimsMissing = (claiming || recon) && sections && !cl;
  return (
    <section aria-labelledby="sc-office-h" className="order-last lg:order-first lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
      <h2 id="sc-office-h" className="text-[13px] font-semibold text-slate-900">Office</h2>
      {loading ? <Skeleton className="h-8 w-64" />
        : items.length ? (
          <ul className="flex flex-wrap gap-2">
            {items.map((x) => (
              <li key={x.key}>
                <Link to={x.to} className={clsx('inline-flex items-center gap-2 h-11 sm:h-8 pl-3 pr-2 rounded-full ring-1 ring-inset text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500', tone(x.tone).pill, 'hover:brightness-95')}>
                  {x.label}
                  <span className="tabular-nums rounded-full bg-white/80 px-1.5 py-px text-[12px] font-bold">{x.money ? money(x.value) : x.value}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-slate-600 flex items-center gap-1.5">
            <CheckCheck size={15} className="text-emerald-600" aria-hidden />
            {claimsMissing ? 'Claim counts couldn’t load. Open Claims to check.' : 'Hours and claims are all up to date'}
          </p>
        )}
      <LinkBtn to={view('view=dashboard')} className="sm:ml-auto">Reports &amp; oversight</LinkBtn>
    </section>
  );
}

// A link that looks like the kit's buttons (the kit Button is a <button>; navigation should be a link).
function LinkBtn({ to, children, variant = 'ghost', size = 'sm', icon: Icon, className, ...rest }) {
  return (
    <Link to={to} {...rest}
      className={clsx('inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl transition-colors duration-150 whitespace-nowrap',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1',
        size === 'sm' ? 'text-[12.5px] h-10 sm:h-8 px-3' : 'text-[13px] h-11 sm:h-9 px-3.5',
        variant === 'secondary' ? 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300'
          : 'text-indigo-700 hover:bg-indigo-50 hover:text-indigo-900', className)}>
      {Icon && <Icon size={size === 'sm' ? 13 : 15} aria-hidden />}
      {children}
    </Link>
  );
}
